import { useEffect, useState } from "react";
import { Category, Classification, Group, OsPoiClassification } from "../../../types/OsPoi/OsPoiTypes";
import OsPoiClassificationList from "./OsPoiClassificationList";
import { Button } from "react-bootstrap";
import { useFormikContext } from "formik";

export interface OsPoiClassificationFieldProps {
    poiChildren: Group[];
}

const OsPoiClassificationsField = ({ poiChildren }: OsPoiClassificationFieldProps) => {
    const { setValues } = useFormikContext<{
    classifications: number[];
    }>();
    const [selectedGroup, setSelectedGroup] = useState<Group | undefined>(undefined);
    const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined);
    const [selectedItems, setSelectedItems] = useState<Classification[]>([]);
    const [topLevelSelections, setTopLevelSelections] = useState<Classification[]>([]);

    useEffect(() => {
        const optimized = distillClassifications(poiChildren, selectedItems);
        setTopLevelSelections(optimized);

        var selectedIds = selectedItems.map(x=>x.id);
        setValues(prevValues => ({
            ...prevValues,
            classifications: selectedIds
        }));
    }, [selectedItems, poiChildren]);

    const onGroupExpanded = (group: OsPoiClassification) => {
        setSelectedGroup(group as Group);
        setSelectedCategory(undefined);
    }

    const onCategoryExpanded = (category: OsPoiClassification) => {
        setSelectedCategory(category as Category);
    }

    const getAllBaseClassificationChildren = (cls: OsPoiClassification): OsPoiClassification[] => {
        if (!cls.children) {
            return [cls];
        }
        return cls.children.flatMap(child => getAllBaseClassificationChildren(child));
    }

    const handleToggleSelected = (classification: OsPoiClassification, selected: boolean) => {
        setSelectedItems(prevItems => {
            if (selected) {
                const allClassificationChildren = getAllBaseClassificationChildren(classification);
                return Array.from(new Set([...prevItems, ...allClassificationChildren]));
            } else {
                const allClassificationChildren = new Set(getAllBaseClassificationChildren(classification));
                return prevItems.filter(item => !allClassificationChildren.has(item));
            }
        });
    }

    const areAllChildrenSelected = (classification: OsPoiClassification) => {
        if (!classification.children) {
            return selectedItems.some(x => x.id === classification.id);
        }

        const baseChildren = getAllBaseClassificationChildren(classification);
        return baseChildren.every(child => 
            selectedItems.some(selected => selected.id === child.id)
        );
    }

    const areAnyChildrenSelected = (classification: OsPoiClassification) => {
        if (!classification.children) {
            return selectedItems.some(x => x.id === classification.id);
        }
        const baseChildren = getAllBaseClassificationChildren(classification);
        return baseChildren.some(child => 
            selectedItems.some(selected => selected.id === child.id)
        );
    }

    const distillClassifications = (
        topLevelClassifications: OsPoiClassification[],
        selectedItems: OsPoiClassification[]
    ): OsPoiClassification[] => {
        const result: OsPoiClassification[] = [];
        const selectedIds = new Set(selectedItems.map(item => item.id));

        for (const group of topLevelClassifications) {
            if (areAllChildrenSelected(group)) {
                result.push(group);
                continue;
            }

            if (group.children) {
                for (const category of group.children) {
                    if (areAllChildrenSelected(category)) {
                        result.push(category);
                        continue;
                    }

                    if (category.children) {
                        for (const classification of category.children) {
                            if (selectedIds.has(classification.id)) {
                                result.push(classification);
                            }
                        }
                    }
                }
            }
        }

        return result;
    }

    return <>
        <div className="d-flex overflow-auto flex-nowrap pb-2">
            <OsPoiClassificationList 
                isAnyChildrenSelected={areAnyChildrenSelected} 
                isAllChildrenSelected={areAllChildrenSelected} 
                handleExpanded={onGroupExpanded} 
                toggleSelected={handleToggleSelected} 
                poiChildren={poiChildren} 
                title="Groups" 
            />
            {selectedGroup && selectedGroup.children && 
                <OsPoiClassificationList 
                    isAnyChildrenSelected={areAnyChildrenSelected} 
                    isAllChildrenSelected={areAllChildrenSelected} 
                    handleExpanded={onCategoryExpanded} 
                    toggleSelected={handleToggleSelected} 
                    poiChildren={selectedGroup.children} 
                    title="Categories" 
                />
            }
            {selectedCategory && selectedCategory.children && 
                <OsPoiClassificationList 
                    isAllChildrenSelected={areAllChildrenSelected} 
                    isAnyChildrenSelected={areAnyChildrenSelected} 
                    toggleSelected={handleToggleSelected} 
                    poiChildren={selectedCategory.children} 
                    title="Classifications" 
                />
            }
        </div>
        <div className="d-flex align-items-start">
            <Button
                style={{ width: 140 }}
                className="flex-shrink-0"
                onClick={() => {
                    setSelectedItems([]);
                    setTopLevelSelections([]);
                }}>
                Reset Selection
            </Button>
            <div className="ms-2 d-flex flex-wrap gap-2">
                {topLevelSelections.map((classification) => (
                    <Button
                        key={classification.id}
                        variant="outline-info"
                        size="sm"
                        style={{ textAlign: "left" }}
                        onClick={() => {
                            handleToggleSelected(classification, false);
                        }}>
                        {classification.children && <b>{classification.description}</b>}
                        {!classification.children && classification.description}
                    </Button>
                ))}
            </div>
        </div>
    </>;
}

export default OsPoiClassificationsField;